import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saidaService, SaidaDetalhes, Saida } from '../../services/saida';

const SaidaUnificar: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const idSaida = Number(id);

  const [notaSaida, setNotaSaida] = useState<SaidaDetalhes | null>(null);
  const [saidasDoVendedor, setSaidasDoVendedor] = useState<Saida[]>([]);
  const [selectedSaida, setSelectedSaida] = useState<Saida | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const { data: saidaData, isLoading, error } = useQuery<SaidaDetalhes>({
    queryKey: ['saida', 'resumido', idSaida],
    queryFn: () => saidaService.getByIdResumido(idSaida),
    enabled: !!idSaida,
  });

  useEffect(() => {
    if (saidaData) {
      setNotaSaida(saidaData);
    }
  }, [saidaData]);

  useEffect(() => {
    if (notaSaida?.idVendedor) {
      saidaService.getSaidasDoVendedor(notaSaida.idVendedor).then((data) => {
        const outrasSaidas = data.filter((s) => (s.idSaida || s.id) !== idSaida);
        setSaidasDoVendedor(outrasSaidas);
      }).catch((err) => {
        console.error('Erro ao carregar saídas do vendedor', err);
      });
    }
  }, [notaSaida, idSaida]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSaida) throw new Error('Selecione uma saída para unificar.');
      const idSaidaUnificar = selectedSaida.idSaida || selectedSaida.id;
      return saidaService.salvarUnificar(idSaida, idSaidaUnificar);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      queryClient.invalidateQueries({ queryKey: ['saida', idSaida] });
      queryClient.invalidateQueries({ queryKey: ['saida', 'resumido', idSaida] });
      setSnackbar({ open: true, message: 'Saídas unificadas com sucesso.', severity: 'success' });
      navigate('/saidas');
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro ao unificar saídas: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSaida) {
      setSnackbar({ open: true, message: 'Selecione uma saída para unificar.', severity: 'error' });
      return;
    }
    saveMutation.mutate();
  };

  const handleBack = () => {
    navigate('/saidas');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !notaSaida) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar a nota de saída.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar a nota de saída.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  const getSaidaLabel = (saida: Saida) => {
    if (saida.textoExibir) return saida.textoExibir;
    return `#${saida.idSaida || saida.id} - ${saida.dataSaidaString || ''} - ${saida.dataPrevistaRetornoString || saida.dataRetornoString || ''}`;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Unificar Saída #{idSaida}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Promotor de vendas"
                value={notaSaida.nomePromotor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Praça"
                value={notaSaida.praca || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Vendedor(a)"
                value={notaSaida.nomeVendedor || notaSaida.vendedor || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="CPF"
                value={notaSaida.cpf || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data de saída"
                value={notaSaida.dataSaidaString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Data de retorno"
                value={notaSaida.dataRetornoString || notaSaida.dataPrevistaRetornoString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={saidasDoVendedor}
                getOptionLabel={getSaidaLabel}
                value={selectedSaida}
                onChange={(_, value) => setSelectedSaida(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Saída para unificar"
                    placeholder="Selecione..."
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="contained"
            type="submit"
            startIcon={<SaveIcon />}
            disabled={saveMutation.isPending || !selectedSaida}
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaidaUnificar;
